<template>
  <div class="dashboard-container">
    <h1>仪表板</h1>
    <p>欢迎使用工单管理系统</p>

    <!-- 加载状态 -->
    <div v-if="loading" class="loading">
      <el-icon class="is-loading">
        <Loading/>
      </el-icon>
      <span>加载中...</span>
    </div>

    <div v-else>
      <!-- 快速操作入口 -->
      <div class="quick-actions">
        <h3>快速操作</h3>
        <el-row :gutter="20">
          <!-- 创建工单入口 -->
          <el-col :xs="24" :sm="8" :md="6">
            <el-card
                class="action-card"
                shadow="hover"
                @click="navigateToCreateWorkOrder">
              <div class="action-content">
                <div class="action-icon create">
                  <el-icon :size="32">
                    <Plus/>
                  </el-icon>
                </div>
                <div class="action-info">
                  <div class="action-title">创建工单</div>
                  <div class="action-desc">提交新的工单请求</div>
                </div>
              </div>
            </el-card>
          </el-col>

          <!-- 用户管理入口 -->
          <el-col :xs="24" :sm="8" :md="6" v-if="isAdmin">
            <el-card
                class="action-card"
                shadow="hover"
                @click="navigateTo('/users')"
            >
              <div class="action-content">
                <div class="action-icon user">
                  <el-icon :size="32">
                    <User/>
                  </el-icon>
                </div>
                <div class="action-info">
                  <div class="action-title">用户管理</div>
                  <div class="action-desc">管理用户和权限</div>
                </div>
              </div>
            </el-card>
          </el-col>

          <!-- 个人信息入口 -->
          <el-col :xs="24" :sm="8" :md="6">
            <el-card
                class="action-card"
                shadow="hover"
                @click="navigateTo('/profile')"
            >
              <div class="action-content">
                <div class="action-icon profile">
                  <el-icon :size="32">
                    <UserFilled/>
                  </el-icon>
                </div>
                <div class="action-info">
                  <div class="action-title">个人信息</div>
                  <div class="action-desc">查看和编辑个人资料</div>
                </div>
              </div>
            </el-card>
          </el-col>

          <!-- 工单列表入口 -->
          <el-col :xs="24" :sm="8" :md="6">
            <el-card
                class="action-card"
                shadow="hover"
                @click="navigateTo('/workorders')"
            >
              <div class="action-content">
                <div class="action-icon list">
                  <el-icon :size="32">
                    <List/>
                  </el-icon>
                </div>
                <div class="action-info">
                  <div class="action-title">工单列表</div>
                  <div class="action-desc">查看所有工单</div>
                </div>
              </div>
            </el-card>
          </el-col>
        </el-row>
      </div>

      <div class="dashboard-cards">
        <h3>统计概览</h3>
        <el-row :gutter="20">
          <el-col :span="6" v-for="item in stats" :key="item.title">
            <el-card class="stat-card">
              <div class="stat-content">
                <div class="stat-icon" :style="{ background: item.color }">
                  <el-icon :size="24">
                    <component :is="item.icon"/>
                  </el-icon>
                </div>
                <div class="stat-info">
                  <div class="stat-value">{{ item.value }}</div>
                  <div class="stat-title">{{ item.title }}</div>
                </div>
              </div>
            </el-card>
          </el-col>
        </el-row>
      </div>

      <!-- 图表区域 -->
      <el-row :gutter="20" class="chart-row">
        <el-col :span="12">
          <el-card>
            <template #header>
              <span>状态分布</span>
            </template>
            <div v-if="statusData.length > 0" class="chart-container">
              <div v-for="item in statusData" :key="item.name" class="chart-item">
                <div class="chart-label">
                  <span>{{ getStatusText(item.name) }}</span>
                  <span class="chart-value">{{ item.value }} ({{ item.percentage }}%)</span>
                </div>
                <el-progress
                    :percentage="item.percentage"
                    :color="getStatusColor(item.name)"
                    :show-text="false"
                />
              </div>
            </div>
            <div v-else class="empty-chart">暂无数据</div>
          </el-card>
        </el-col>

        <el-col :span="12">
          <el-card>
            <template #header>
              <span>环境分布</span>
            </template>
            <div v-if="environmentData.length > 0" class="chart-container">
              <div v-for="item in environmentData" :key="item.originalName" class="chart-item">
                <div class="chart-label">
                  <span>{{ item.name }}</span>  <!-- 显示中文名称 -->
                  <span class="chart-value">{{ item.value }} ({{ item.percentage }}%)</span>
                </div>
                <el-progress
                    :percentage="item.percentage"
                    :color="getEnvColor(item.originalName)"
                :show-text="false"
                />
              </div>
            </div>
            <div v-else class="empty-chart">暂无数据</div>
          </el-card>
        </el-col>
      </el-row>

      <div class="recent-orders">
        <el-card>
          <template #header>
            <div class="card-header">
              <span>最近工单</span>
              <el-button type="text" @click="refreshData">刷新</el-button>
            </div>
          </template>
          <el-table
              :data="recentOrders"
              style="width: 100%"
              v-loading="loadingOrders"
              @row-click="handleRowClick"
          >
            <el-table-column prop="order_number" label="工单号" width="120">
              <template #default="{ row }">
                <el-tag type="info" size="small">{{ row.order_number }}</el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="title" label="标题" min-width="200"/>
            <el-table-column prop="status" label="状态" width="100">
              <template #default="{ row }">
                <el-tag :type="getStatusType(row.status)" size="small">
                  {{ getStatusText(row.status) }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="created_at" label="创建时间" width="160">
              <template #default="{ row }">
                {{ formatDate(row.created_at) }}
              </template>
            </el-table-column>
            <el-table-column label="操作" width="80">
              <template #default="{ row }">
                <el-button type="text" size="small" @click.stop="viewOrder(row)">
                  查看
                </el-button>
              </template>
            </el-table-column>
          </el-table>
        </el-card>
      </div>
    </div>
  </div>
</template>

<script setup>
import {computed, onMounted, ref} from 'vue'
import {useRouter} from 'vue-router'
import {ElMessage} from 'element-plus'
import {Check, Clock, List, Loading, Plus, Ticket, User, UserFilled, Warning} from '@element-plus/icons-vue'
import {useAuthStore} from '@/stores/auth'
import {getWorkOrders, getWorkOrderStats} from '@/api/workorder'
import dayjs from 'dayjs'
import {checkPermission} from "@/api/auth";

const router = useRouter()
const authStore = useAuthStore()

// 响应式数据
const loading = ref(false)
const loadingOrders = ref(false)
const statsData = ref({})
const recentOrders = ref([])

// 计算属性
const currentUser = computed(() => authStore.user || {})
const isAdmin = computed(() => currentUser.value.role === 'admin' || currentUser.value.is_admin)

const stats = computed(() => {
  if (!statsData.value.total) {
    return [
      {title: '总工单数', value: 0, icon: Ticket, color: '#409eff'},
      {title: '待处理', value: 0, icon: Clock, color: '#e6a23c'},
      {title: '处理中', value: 0, icon: Warning, color: '#f56c6c'},
      {title: '已解决', value: 0, icon: Check, color: '#67c23a'}
    ]
  }

  const status = statsData.value.by_status || {}
  return [
    {
      title: '总工单数',
      value: statsData.value.total || 0,
      icon: Ticket,
      color: '#409eff'
    },
    {
      title: '待处理',
      value: status.open || 0,
      icon: Clock,
      color: '#e6a23c'
    },
    {
      title: '处理中',
      value: status.in_progress || 0,
      icon: Warning,
      color: '#f56c6c'
    },
    {
      title: '已解决',
      value: status.resolved || 0,
      icon: Check,
      color: '#67c23a'
    }
  ]
})

const statusData = computed(() => {
  const status = statsData.value.by_status || {}
  const total = statsData.value.total || 1

  return Object.entries(status).map(([key, value]) => ({
    name: key,
    value,
    percentage: Math.round((value / total) * 100)
  }))
})

const environmentData = computed(() => {
  const env = statsData.value.by_environment || {}
  const total = statsData.value.total || 1

  return Object.entries(env).map(([key, value]) => ({
    name: getEnvironmentChineseName(key),  // 使用映射后的中文名称
    value,
    percentage: Math.round((value / total) * 100),
    originalName: key  // 保留原始名称，用于颜色映射等
  }))
})

// 环境代码到中文名称的映射
const getEnvironmentChineseName = (envCode) => {
  const envMap = {
    'test': '测试环境',
    'staging': '预发环境',
    'production': '生产环境',
    'dev': '开发环境',
    'development': '开发环境',
    'default': '默认环境'
    // 可以继续添加其他环境映射
  }

  // 如果 envCode 包含中文，直接返回
  if (/[\u4e00-\u9fa5]/.test(envCode)) {
    return envCode
  }

  // 否则返回映射的中文名称，如果没有映射则返回原值
  return envMap[envCode] || envCode
}

// 方法
const loadStats = async () => {
  try {
    loading.value = true
    const response = await getWorkOrderStats()

    // 根据API响应结构调整
    if (response.data) {
      statsData.value = response.data
    } else {
      statsData.value = response
    }

  } catch (error) {
    console.error('加载统计失败:', error)
    ElMessage.error(error.response?.data?.message || '加载统计数据失败')
  } finally {
    loading.value = false
  }
}

const navigateToCreateWorkOrder = async () => {
  try {
    // 检查权限
    // console.log(111)
    const permissionResponse = await checkPermission(
        'workorder.create'
    )

    if (permissionResponse.has_permission || permissionResponse.data.has_permission) {
      await router.push('/workorders/create')
    } else {
      ElMessage.warning('您没有创建工单的权限')
    }
  } catch (error) {
    console.error('权限检查失败:', error)
    ElMessage.error('权限检查失败')
  }
}

const loadRecentOrders = async () => {
  try {
    loadingOrders.value = true

    // 查询本人的待处理、处理中、待确认状态的工单
    const myId = authStore.user?.id
    const statuses = ['open', 'in_progress', 'pending']

    // 后端列表接口 status 为单值参数，分别查询三种状态后合并
    const results = await Promise.all(
        statuses.map(status => getWorkOrders({
          page: 1,
          per_page: 5,
          status,
          sort_by: 'created_at',
          sort_order: 'desc'
        }))
    )

    let orders = []
    results.forEach(res => {
      const list = res?.data?.workorders || res?.workorders || []
      orders = orders.concat(list)
    })

    // 仅保留本人的工单（创建人或当前处理人为当前登录用户）
    if (myId) {
      orders = orders.filter(o => o.creator_id === myId || o.current_handler_id === myId)
    }

    // 按创建时间倒序排列，取最近5条
    orders.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    recentOrders.value = orders.slice(0, 5)

  } catch (error) {
    console.error('加载最近工单失败:', error)
    ElMessage.error(error.response?.data?.message || error.response?.data?.detail || '加载最近工单失败')
  } finally {
    loadingOrders.value = false
  }
}

// 刷新按钮：仅刷新最近工单列表，不触发全局刷新
const refreshData = () => {
  loadRecentOrders()
  ElMessage.success('最近工单已刷新')
}

const navigateTo = (path) => {
  router.push(path)
}

const handleRowClick = (row) => {
  viewOrder(row)
}

const viewOrder = (order) => {
  router.push(`/workorders/${order.id}`)
}

const getStatusType = (status) => {
  const types = {
    open: 'info',
    in_progress: 'warning',
    pending: '',
    resolved: 'success',
    closed: 'danger'
  }
  return types[status] || ''
}

const getStatusText = (status) => {
  const texts = {
    open: '待处理',
    in_progress: '处理中',
    pending: '待确认',
    resolved: '已解决',
    closed: '已关闭',
    deleted: '已删除'
  }
  return texts[status] || status
}

const getStatusColor = (status) => {
  const colors = {
    open: '#e6a23c',    // 橙色
    in_progress: '#f56c6c', // 红色
    resolved: '#67c23a', // 绿色
    closed: '#909399',   // 灰色
    pending: '#409eff'   // 蓝色
  }
  return colors[status] || '#409eff'
}

const getEnvColor = (env) => {
  const colors = {
    'production': '#f56c6c',   // 红色
    '生产环境': '#f56c6c',      // 中文也支持
    'test': '#409eff',         // 蓝色
    '测试环境': '#409eff',
    'staging': '#e6a23c',      // 橙色
    '预发环境': '#e6a23c',
    'dev': '#67c23a',          // 绿色
    '开发环境': '#67c23a'
  }

  // 如果传入的是中文名称，直接使用
  if (colors[env]) {
    return colors[env]
  }

  // 否则尝试获取映射后的名称对应的颜色
  const chineseName = getEnvironmentChineseName(env)
  return colors[chineseName] || '#909399'
}

const formatDate = (date) => {
  if (!date) return ''
  return dayjs(date).format('YYYY-MM-DD HH:mm')
}

// 生命周期
onMounted(async () => {
  try {
    // 确保用户信息完整
    await authStore.ensureUserInfoComplete()

    // 然后加载数据
    await Promise.all([
      loadStats(),
      loadRecentOrders()
    ])
  } catch (error) {
    console.error('初始化仪表板失败:', error)
    ElMessage.error('加载数据失败')
  }
})
</script>

<style scoped lang="scss">
.dashboard-container {
  padding: 20px;

  h1 {
    margin: 0 0 8px 0;
    font-size: 28px;
    color: var(--text-primary);
  }

  p {
    margin: 0 0 30px 0;
    color: var(--text-secondary);
  }

  .loading {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 40px;
    color: var(--text-secondary);

    .el-icon {
      margin-right: 10px;
      animation: rotating 2s linear infinite;
    }
  }

  .quick-actions {
    margin-bottom: 30px;

    h3 {
      margin: 0 0 16px 0;
      font-size: 18px;
      color: var(--text-primary);
    }

    .action-card {
      margin-bottom: 20px;
      cursor: pointer;
      transition: all var(--transition-base);
      perspective: 800px;

      &:hover {
        transform: translateY(-6px) rotateX(2deg);
        box-shadow: var(--shadow-lg);
      }

      &:active {
        transform: translateY(-2px) scale(0.98);
      }

      .action-content {
        display: flex;
        align-items: center;
        padding: 16px;

        .action-icon {
          width: 64px;
          height: 64px;
          border-radius: 16px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-right: 16px;
          transition: transform var(--transition-base);

          &.create {
            background: var(--gradient-primary);
          }

          &.user {
            background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
          }

          &.profile {
            background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
          }

          &.list {
            background: var(--gradient-success);
          }

          .el-icon {
            color: white;
          }
        }

        &:hover .action-icon {
          transform: scale(1.08) rotate(3deg);
        }

        .action-info {
          flex: 1;

          .action-title {
            font-size: 16px;
            font-weight: 600;
            color: var(--text-primary);
            margin-bottom: 4px;
          }

          .action-desc {
            font-size: 12px;
            color: var(--text-secondary);
          }
        }
      }
    }
  }

  .dashboard-cards {
    margin-bottom: 30px;

    h3 {
      margin: 0 0 16px 0;
      font-size: 18px;
      color: var(--text-primary);
    }

    .stat-card {
      margin-bottom: 20px;
      transition: all var(--transition-base);

      &:hover {
        transform: translateY(-3px);
        box-shadow: var(--shadow-md);
      }

      .stat-content {
        display: flex;
        align-items: center;

        .stat-icon {
          width: 48px;
          height: 48px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-right: 16px;

          .el-icon {
            color: white;
          }
        }

        .stat-info {
          .stat-value {
            font-size: 24px;
            font-weight: 600;
            color: var(--text-primary);
            margin-bottom: 4px;
          }

          .stat-title {
            font-size: 14px;
            color: var(--text-secondary);
          }
        }
      }
    }
  }

  .chart-row {
    margin-bottom: 30px;
  }

  .chart-container {
    .chart-item {
      margin-bottom: 15px;

      &:last-child {
        margin-bottom: 0;
      }

      .chart-label {
        display: flex;
        justify-content: space-between;
        margin-bottom: 5px;
        font-size: 14px;

        .chart-value {
          color: var(--text-secondary);
          font-size: 12px;
        }
      }
    }
  }

  .empty-chart {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 200px;
    color: var(--text-secondary);
  }

  .recent-orders {
    .card-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
    }

    :deep(.el-table__row) {
      cursor: pointer;
    }
  }
}

@keyframes rotating {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>